import { Component, Input } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateFormatPipe } from 'ngx-moment';
import { DuplicateItemActions } from 'src/app/enums/duplicate-item-actions.enum';
import { QuestionnaireMasterAvailability } from 'src/app/enums/questionnaire-master-availability.enum';
import { QuestionnaireMasterStatus } from 'src/app/enums/questionnaire-master-status.enum';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { DuplicateItemQfmSections } from './models/duplicate-item-qfm-sections.model';
import { DuplicateItemForm } from './models/duplicate-item-form.model';
import { MAX_QFM_TITLE_LENGTH } from 'src/app/constants/questionnaire.constants';
import { EdlEntriesFormBuilderService, EdlEntriesFormGroup, EdlEntry, EdlTemplateTypeDTO, PagedResponse, PortalSettings, SessionService } from '@edl-development/edl-common';
import { CplItemPermissions } from 'src/app/model/enum/cpl-item-permissions.enum';
import { ModalService } from '../modal.service';

@Component({
    selector: 'edl-modal-duplicate-item',
    templateUrl: './edl-modal-duplicate-item.component.html',
    styleUrls:   ['./edl-modal-duplicate-item.component.scss'],
})
export class EdlModalDuplicateItem {
    @Input() modalTitle: string;
    @Input() itemTitle: string;
    @Input() maxTitleLength: number;
    @Input() questionnaireSections: DuplicateItemQfmSections[];
    @Input() status: QuestionnaireMasterStatus;
    @Input() availability: QuestionnaireMasterAvailability;
    @Input() isTemplate: boolean;
    @Input() toTemplate: boolean;
    @Input() cplItemPermissions: CplItemPermissions[] = [];

    form: UntypedFormGroup;
    questionnaires: any[]
    canDuplicateItemPackets: boolean = false;
    portalSettings: PortalSettings;

    CplItemPermissions = CplItemPermissions;
    DuplicateItemActions = DuplicateItemActions;
    QuestionnaireMasterAvailability = QuestionnaireMasterAvailability;

    constructor(
        public activeModal: NgbActiveModal,
        private questionnaireService: QuestionnaireService,
        private sessionService: SessionService,
        private formBuilder: UntypedFormBuilder,
        private edlEntryFormBuilderService: EdlEntriesFormBuilderService,
        private modalService: ModalService
    ) {}

    get EdlTemplateTypesForm() {
        return this.form.get('EdlTemplateTypes') as EdlEntriesFormGroup<EdlTemplateTypeDTO>;
    }

    ngOnInit() {
        this.portalSettings = this.sessionService.PortalSettings;

        if (this.questionnaireSections.length > 0) {
            this.questionnaireService
                .getQuestionnairesForPortal({
                    portalId: this.sessionService.user.UserPortal.PortalID,
                    availability: this.availability,
                    offset: 0,
                    limit: 10000,
                    status: this.status,
                    orderBy: 'Title',
                    ascending: true
                }).subscribe((results: PagedResponse) => {
                    this.questionnaires = results.Results;
                });
        }

        // Check if the user has permission to duplicate item packets
        this.canDuplicateItemPackets = !this.isTemplate 
            && !this.toTemplate 
            && this.cplItemPermissions.includes(CplItemPermissions.CAN_EDIT_ITEM_PACKETS);

        this.generateForm(this.questionnaireSections, this.itemTitle, this.canDuplicateItemPackets);
    }

    private generateForm(
        questionnaireSections: DuplicateItemQfmSections[], 
        itemTitle: string, 
        canDuplicateItemPackets: boolean
    ) {
        const today = new Date();

        const sections = new UntypedFormArray([]);
        questionnaireSections?.forEach(section => {
            const questionnaires = new UntypedFormArray([]);

            section.questionnaires?.forEach(questionnaire => {
                const questionnaireForm = this.formBuilder.group({
                    action: [this.toTemplate !== questionnaire.IsTemplate ? DuplicateItemActions.DUPLICATE : DuplicateItemActions.KEEP, [Validators.required]],
                    oldQfmId: [questionnaire.QfmId, []],
                    newQfmId: [null, []],
                    duplicateQfmTitle: [this.generateTitle(questionnaire.QfmTitle, today, !questionnaire.IsTemplate, MAX_QFM_TITLE_LENGTH), [Validators.maxLength(MAX_QFM_TITLE_LENGTH)]],
                });
                questionnaireForm.setValidators(this.isQuestionnaireValid());
                questionnaires.push(questionnaireForm);
            });

            const sectionGroup = this.formBuilder.group({
                title: [section.title, []],
                questionnaires
            });

            sections.push(sectionGroup);
        });

        this.form = this.formBuilder.group({
            title: [this.generateTitle(itemTitle, today, this.toTemplate, this.maxTitleLength), [Validators.required, Validators.maxLength(this.maxTitleLength)]],
            shouldDuplicateRoster: [false, []],
            shouldDupItemPackets: [canDuplicateItemPackets, []],
            sections,
            EdlTemplateTypes: this.edlEntryFormBuilderService.buildSelectedFormGroup([])
        });

        // When EdlTemplateTypesForm changes, all questionnaires need to be validated to make sure the are compatable with the new template types
        this.EdlTemplateTypesForm.valueChanges.subscribe(() => {
            this.validateAllQuestionnaires();
        });
    }

    private generateTitle(baseTitle: string, today: Date, isTemplate: boolean, maxTotalTitleLength: number) {
        const dateFormatPipe = new DateFormatPipe();
        const postfix = ` ${ isTemplate ? 'Template' : 'Copy' } (${ dateFormatPipe.transform(today, 'MM/DD/YYYY hh:mm a') })`;
        const maxBaseTitleLength = maxTotalTitleLength - postfix.length;
        const adjustedBaseTitle = baseTitle.substring(0, maxBaseTitleLength);

        return adjustedBaseTitle + postfix;
    }

    public getQuestionnaireErrors(sectionIndex: number, quesitonnaireIndex: number, errorName: string) {
        const errors = this.getQuestionnaireGroup(sectionIndex, quesitonnaireIndex).errors;
        return errors ? errors[errorName] : null;
    } 

    public getQuestionnaireField(sectionIndex: number, quesitonnaireIndex: number, quesitonnaireGroupKey: string) {
        return this.getQuestionnaireGroup(sectionIndex, quesitonnaireIndex).get(quesitonnaireGroupKey);
    }

    /** Whether any questionnaire in this section currently needs the "New questionnaire" column (a Duplicate title input or a Replace-with select) — used to collapse the table to two columns when no row needs it. */
    public sectionHasNewQuestionnaireColumn(sectionIndex: number): boolean {
        const questionnaires = (<UntypedFormArray>this.form.get(['sections', sectionIndex, 'questionnaires'])).controls;

        return questionnaires.some(questionnaire => {
            const action = questionnaire.get('action').value;
            // Loose equality: the native <select> here binds options via [value] rather than [ngValue],
            // so a user-driven change round-trips the enum through the DOM as a string (matches the
            // == comparisons used elsewhere in this component, e.g. getQuestionnaireField(...).value == ...).
            return action == DuplicateItemActions.DUPLICATE || action == DuplicateItemActions.REPLACE;
        });
    }

    private getQuestionnaireGroup(sectionIndex: number, quesitonnaireIndex: number) {
        return this.form.get(['sections', sectionIndex, 'questionnaires', quesitonnaireIndex]);
    }

    public submit(form: FormGroup) {
        if (this.form.valid) {
            const value = this.packageForm(form);
            this.activeModal.close(value);
        }
    }

    private packageForm(form: FormGroup): DuplicateItemForm {
        const value = form.value;
        value.EdlTemplateTypes = this.edlEntryFormBuilderService
            .toSelectedEntries(form.get('EdlTemplateTypes') as EdlEntriesFormGroup<EdlTemplateTypeDTO>)
            .map(entry => entry.details);
        
        return value;
    }

    private isQuestionnaireValid(): ValidatorFn {
        return (c: AbstractControl): {[key: string]: any} => {
            const action = c.get('action').value;

            switch (action.toString()) {
                case DuplicateItemActions.REPLACE.toString():
                    const newQfmId = c.get('newQfmId').value;
                    if (!newQfmId) {
                        return { newQfmIdRequired: true };
                    }

                    // If not a template, no need to validate questionnaire template types
                    if (!this.toTemplate) {
                        return null;
                    }

                    // Validate that the questionnaire template can show up on all the same portals as the new template
                    const questionnaire = this.questionnaires.find(q => q.FormID === newQfmId);
                    const questionnaireTemplateTypes: EdlTemplateTypeDTO[] = questionnaire?.EdlTemplateTypes ?? [];

                    // If there are no template types, the the questionnaire template can appear on all portals
                    if (questionnaireTemplateTypes.length === 0) {
                        return null;
                    }

                    const selectedTemplateTypes = this.edlEntryFormBuilderService.toSelectedEntries(this.EdlTemplateTypesForm).map(e => e.details);

                    if (selectedTemplateTypes.length === 0) {
                        return { templateTypeMismatch: 'Only questionnaires that are available on all portals can be used with this item.' };
                    }

                    const missingTemplateTypes = selectedTemplateTypes.filter(t => questionnaireTemplateTypes.findIndex(qt => qt.Id == t.Id) === -1);

                    if(missingTemplateTypes.length !== 0) {
                        const missingTemplateTypesMessage = `This questionnaire is missing the following template types: ${missingTemplateTypes.map(t => t.Title).join(', ')}`;
                        return { templateTypeMismatch: missingTemplateTypesMessage };
                    }

                    return null;
                case DuplicateItemActions.DUPLICATE.toString():
                    return !c.get('duplicateQfmTitle').value ? { duplicateQfmTitleRequired: true} : null;
                case DuplicateItemActions.KEEP.toString():
                default:
                    return null;
            }
        };
    }

    addTemplateTypes() {
        const edlEntries: EdlEntry<EdlTemplateTypeDTO>[] = (this.portalSettings?.EdlTemplateTypes ?? [])
            .map((entry: EdlTemplateTypeDTO) => ({
                id: entry.Id,
                name: entry.Title,
                details: entry
            }));

        const selectedEntries = this.edlEntryFormBuilderService.toSelectedEntries(this.EdlTemplateTypesForm);

        this.modalService
            .edlEntriesSelectorFormModal({ edlEntries, selectedEntries, title: 'Select Template Types' })
            .then((result: EdlEntry<EdlTemplateTypeDTO>[]) => {
                this.edlEntryFormBuilderService.updateSelectedFormGroup(this.EdlTemplateTypesForm, result);
            }, () => { });
    }

    private validateAllQuestionnaires = () => {
        (<UntypedFormArray>this.form.get('sections')).controls.forEach((section) => {
            (<UntypedFormArray>section.get('questionnaires')).controls.forEach((questionnaire) => {
                questionnaire.updateValueAndValidity();
            });
        });
    }
}